/**
 * Build a truthful Device Health History timeline for Chart.js.
 *
 * Observations are periodic snapshots. We plot discrete observed points and
 * connect consecutive samples with a step only when the gap between them is
 * within {@link MAX_OBSERVATION_GAP_MS}. Larger gaps become null breaks so the
 * chart does not invent continuous Online/Offline across missing data.
 */

import {isDeviceHealthOfflineConnectivityLabel, isDeviceHealthOnlineLabel,} from './device-health-status.helpers';

export const MAX_OBSERVATION_GAP_MS = 30 * 60 * 1000;

/** Matches DeviceHealthHistoryPagination.max_page_size. */
export const HISTORY_CHART_PAGE_SIZE = 100;

/**
 * Hard cap on observation pages fetched for the chart (browser + API safety).
 * 25 × 100 = 2 500 samples — enough for typical 7–14 day windows at ~5–15 min cadence.
 */
export const HISTORY_CHART_MAX_PAGES = 25;

export const HISTORY_CHART_MAX_FETCH = HISTORY_CHART_PAGE_SIZE * HISTORY_CHART_MAX_PAGES;

/** Soft cap on Chart.js points after transition-preserving downsample. */
export const HISTORY_CHART_MAX_DISPLAY_POINTS = 750;

export type TimelineStatusKind = 'online' | 'offline' | 'other' | 'gap';

export interface TimelineObservationInput {
    observed_at?: string | null;
    connection_status?: string | null;
    primary_status?: string | null;
    observation_kind?: string | null;
}

export interface ChartObservationRow extends TimelineObservationInput {
    id?: number | string | null;
}

export interface ChartCoverageMeta {
    totalRecords: number;
    fetchedCount: number;
    displayCount: number;
    hitFetchCap: boolean;
    downsampled: boolean;
    pagesFetched: number;
}

/**
 * Consecutive page numbers to request for chart coverage (1-based).
 * API returns newest-first, so pages 1..N are the newest samples in the window.
 * When totalPages exceeds maxPages we stop at the cap and surface hitFetchCap
 * rather than inventing middle-range coverage (which would create false gaps).
 */
export function chartPagesToFetch(totalPages: number, maxPages: number = HISTORY_CHART_MAX_PAGES): number[] {
    const pages = Math.max(0, Math.floor(totalPages));
    const cap = Math.max(1, Math.floor(maxPages));
    if (pages <= 0) {
        return [];
    }
    const count = Math.min(pages, cap);
    return Array.from({length: count}, (_, i) => i + 1);
}

/**
 * Merge newest-first page payloads, dedupe by id (or observed_at+status), chronological sort.
 */
export function mergeObservationPages(pages: ChartObservationRow[][]): ChartObservationRow[] {
    const byKey = new Map<string, ChartObservationRow>();
    for (const page of pages) {
        for (const row of page) {
            if (!row?.observed_at) {
                continue;
            }
            const key = row.id != null
                ? `id:${row.id}`
                : `t:${row.observed_at}|${row.connection_status || ''}|${row.primary_status || ''}|${row.observation_kind || ''}`;
            byKey.set(key, row);
        }
    }
    return Array.from(byKey.values()).sort((a, b) => {
        const ta = Date.parse(String(a.observed_at));
        const tb = Date.parse(String(b.observed_at));
        return ta - tb;
    });
}

/**
 * Keep endpoints and every status-kind transition; thin steady runs if still over maxPoints.
 * Does not invent observations or fill gaps.
 */
export function downsampleObservationsPreservingTransitions(
    chronological: ChartObservationRow[],
    maxPoints: number = HISTORY_CHART_MAX_DISPLAY_POINTS,
): ChartObservationRow[] {
    if (chronological.length <= maxPoints) {
        return chronological;
    }
    if (maxPoints < 2) {
        return chronological.slice(0, Math.max(0, maxPoints));
    }

    const kindOf = (row: ChartObservationRow) =>
        classifyTimelineStatus(
            row.connection_status || row.primary_status,
            row.observation_kind,
        ).kind;

    const keep = new Array(chronological.length).fill(false);
    keep[0] = true;
    keep[chronological.length - 1] = true;
    for (let i = 1; i < chronological.length; i++) {
        if (kindOf(chronological[i]) !== kindOf(chronological[i - 1])) {
            keep[i] = true;
            keep[i - 1] = true;
        }
    }

    let selected = chronological.filter((_, i) => keep[i]);
    if (selected.length < maxPoints) {
        // Still room: add evenly spaced samples from non-kept steady points.
        const remaining = maxPoints - selected.length;
        const candidates = chronological
            .map((row, i) => ({row, i}))
            .filter((c) => !keep[c.i]);
        if (candidates.length > 0 && remaining > 0) {
            const step = Math.max(1, Math.ceil(candidates.length / remaining));
            for (let i = 0; i < candidates.length && selected.length < maxPoints; i += step) {
                keep[candidates[i].i] = true;
                selected = chronological.filter((_, idx) => keep[idx]);
            }
        }
        return selected.slice(0, maxPoints);
    }

    // Too many transitions alone: evenly sample the transition set (still real observations).
    const out: ChartObservationRow[] = [selected[0]];
    const inner = selected.slice(1, -1);
    const innerBudget = maxPoints - 2;
    if (innerBudget > 0 && inner.length > 0) {
        const step = Math.max(1, Math.floor(inner.length / innerBudget));
        for (let i = 0; i < inner.length && out.length < maxPoints - 1; i += step) {
            out.push(inner[i]);
        }
    }
    out.push(selected[selected.length - 1]);
    return out;
}

export interface TimelinePoint {
    t: number;
    label: string;
    kind: TimelineStatusKind;
    y: number | null;
}

/** Numeric bands for stepped chart (null = gap / missing). */
export const TIMELINE_Y = {
    online: 3,
    offline: 1,
    other: 2,
    gap: null as number | null,
} as const;

export function classifyTimelineStatus(
    connectionOrPrimary: string | null | undefined,
    observationKind?: string | null,
): { kind: TimelineStatusKind; label: string; y: number | null } {
    if (observationKind === 'inventory_absent') {
        return {kind: 'other', label: 'Missing from inventory', y: TIMELINE_Y.other};
    }
    const raw = (connectionOrPrimary || '').trim();
    if (!raw) {
        return {kind: 'other', label: 'Unknown', y: TIMELINE_Y.other};
    }
    if (isDeviceHealthOnlineLabel(raw)) {
        return {kind: 'online', label: raw, y: TIMELINE_Y.online};
    }
    if (isDeviceHealthOfflineConnectivityLabel(raw)) {
        return {kind: 'offline', label: raw, y: TIMELINE_Y.offline};
    }
    return {kind: 'other', label: raw, y: TIMELINE_Y.other};
}

/**
 * Convert API observations (newest-first or mixed) into chronological timeline
 * points with explicit null gaps where sampling is discontinuous.
 */
export function buildTimelinePoints(
    observations: TimelineObservationInput[],
    maxGapMs: number = MAX_OBSERVATION_GAP_MS,
): TimelinePoint[] {
    const parsed: TimelinePoint[] = [];
    for (const row of observations) {
        if (!row.observed_at) {
            continue;
        }
        const t = Date.parse(row.observed_at);
        if (Number.isNaN(t)) {
            continue;
        }
        const status = classifyTimelineStatus(
            row.connection_status || row.primary_status,
            row.observation_kind,
        );
        parsed.push({
            t,
            label: status.label,
            kind: status.kind,
            y: status.y,
        });
    }
    parsed.sort((a, b) => a.t - b.t);

    if (parsed.length === 0) {
        return [];
    }

    const withGaps: TimelinePoint[] = [parsed[0]];
    for (let i = 1; i < parsed.length; i++) {
        const prev = parsed[i - 1];
        const curr = parsed[i];
        if (curr.t - prev.t > maxGapMs) {
            // Break the step series so Chart.js does not draw across the gap.
            withGaps.push({
                t: prev.t + 1,
                label: 'No observation',
                kind: 'gap',
                y: null,
            });
            withGaps.push({
                t: curr.t - 1,
                label: 'No observation',
                kind: 'gap',
                y: null,
            });
        }
        withGaps.push(curr);
    }
    return withGaps;
}

export function timelineLegendKinds(points: TimelinePoint[]): TimelineStatusKind[] {
    const seen = new Set<TimelineStatusKind>();
    for (const point of points) {
        if (point.kind !== 'gap') {
            seen.add(point.kind);
        }
    }
    if (points.some((p) => p.kind === 'gap')) {
        seen.add('gap');
    }
    return ['online', 'offline', 'other', 'gap'].filter((k) =>
        seen.has(k as TimelineStatusKind),
    ) as TimelineStatusKind[];
}
